import { createContext, useContext, type ReactNode } from "react";
import type { EditorConfig } from "./types";

/**
 * Context that exposes the active `EditorConfig` (the block-type registry)
 * to descendants of the editor. Used by *container* blocks — Row in
 * particular — that need to look up sibling block types in order to render
 * their nested child blocks.
 *
 * Both `<BlockEditor>` and `<BlockRenderer>` wrap their children with the
 * provider, so a Row block authored inside an article gets the article
 * config; the same Row inside a future document editor would get the
 * document config without changes.
 *
 * Defaults to `null`. Container blocks should fall back gracefully when no
 * provider is in scope (e.g. by rendering nothing rather than crashing).
 */
const BlockTypeContext = createContext<EditorConfig | null>(null);

export function BlockTypeProvider({
  config,
  children,
}: {
  config: EditorConfig;
  children: ReactNode;
}) {
  return <BlockTypeContext.Provider value={config}>{children}</BlockTypeContext.Provider>;
}

export function useBlockTypeContext(): EditorConfig | null {
  return useContext(BlockTypeContext);
}
